/**
 * ActivateRSModal — Jennifer activates a trainee RS as a permanent pool member.
 *
 * Per Greg's rule (May 1): first activation is permanent. The new member's
 * share is set; active members rebalance equally to make room.
 *
 * Math: if trainee share = X (0..1) and there are N active members at share Si,
 * then each Si becomes Si - (X / N). New trainee row flips to status='active',
 * share=X. Total Σshares stays at 1.0.
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 11, §10c #8)
 */

import { useState } from "react";
import { supabase } from "../../lib/supabase";
import { fmtUSD } from "../../lib/analytics";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import { useAuth } from "../../context/AuthContext";

interface RSMember {
  id: string;
  team_member_id: string;
  status: string;
  share: number | null;
}

const SAMPLE_POOL_AMOUNT = 367.50;  // for the "you'll get $X on this deal" preview

export default function ActivateRSModal({
  trainee,
  activeMembers,
  memberMap,
  onClose,
  onActivated,
}: {
  trainee: RSMember;
  activeMembers: RSMember[];
  memberMap: Record<string, { full_name: string }>;
  onClose: () => void;
  onActivated: () => void;
}) {
  const { member } = useAuth();
  const [proposedSharePct, setProposedSharePct] = useState(30);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newShare = proposedSharePct / 100;
  const reductionPerActive = activeMembers.length > 0 ? newShare / activeMembers.length : 0;
  const validProposed = proposedSharePct > 0 && proposedSharePct < 100;
  const allActiveStillPositive = activeMembers.every((m) => (m.share ?? 0) - reductionPerActive > 0.001);
  const traineeName = memberMap[trainee.team_member_id]?.full_name ?? "—";

  async function activate() {
    if (!validProposed) {
      setError("Pick a share between 1% and 99%.");
      return;
    }
    if (!allActiveStillPositive) {
      setError("Proposed share is too high — at least one active member would drop below 0.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // activated_by + actor_id reference team_roster(id), not auth.users(id).
      if (!member?.id) throw new Error("Team roster row not found.");
      const actorId = member.id;

      const now = new Date().toISOString();

      // 1) Activate the trainee
      const { error: traineeErr } = await supabase
        .from("rs_pool_members")
        .update({
          status: "active",
          share: newShare,
          activated_at: now,
          activated_by: actorId,
          notes: reason || null,
        })
        .eq("id", trainee.id);
      if (traineeErr) throw traineeErr;

      // 2) Rebalance each active member down equally
      for (const m of activeMembers) {
        const newActiveShare = (m.share ?? 0) - reductionPerActive;
        const { error: mErr } = await supabase
          .from("rs_pool_members")
          .update({ share: newActiveShare })
          .eq("id", m.id);
        if (mErr) throw mErr;
      }

      // 3) Audit log entry
      await supabase.from("finance_audit_log").insert({
        actor_id: actorId,
        action: "rs_pool_member_activated",
        is_financial: true,
        before_jsonb: { team_member_id: trainee.team_member_id, status: "training", share: 0 },
        after_jsonb: {
          team_member_id: trainee.team_member_id,
          status: "active",
          share: newShare,
          rebalance_reduction_per_active: reductionPerActive,
          active_members_after: activeMembers.length + 1,
        },
        reason: reason || `Activated ${traineeName} at ${proposedSharePct}% — active members rebalanced equally.`,
      });

      onActivated();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="text-base font-semibold text-bardin-ink">Activate {traineeName} as permanent RS</h2>
          <p className="text-xs text-slate-500">First activation is permanent — every future RS Pool defaults to the new split.</p>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800">
            ⚠ <strong>This is a permanent change.</strong> {traineeName} joins as an active RS and the pool default rebalances on every future deal.
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">
              {traineeName}'s share of pool
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={99}
                step={5}
                value={proposedSharePct}
                onChange={(e) => setProposedSharePct(parseFloat(e.target.value) || 0)}
                className="w-24 text-sm border border-slate-300 rounded px-2 py-1 text-right"
              />
              <span className="text-sm">% of pool</span>
              <span className="ml-3 text-xs text-slate-500">
                → {fmtUSD(newShare * SAMPLE_POOL_AMOUNT)} on a typical $24.5k deal
              </span>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-2">
              Resulting active member shares
            </div>
            <div className="space-y-1.5">
              {activeMembers.map((m) => {
                const newShareAfter = (m.share ?? 0) - reductionPerActive;
                const wasPct = ((m.share ?? 0) * 100).toFixed(0);
                const newPct = (newShareAfter * 100).toFixed(0);
                return (
                  <div key={m.id} className="flex items-center justify-between bg-slate-50 rounded p-2 text-sm">
                    <span>
                      <strong>{memberMap[m.team_member_id]?.full_name ?? "—"}</strong>
                      <span className="text-slate-500 text-xs ml-1">Was: {wasPct}%</span>
                    </span>
                    <span className="font-semibold text-slate-700">
                      {newPct}%
                      <span className="text-slate-400 text-xs font-normal ml-1">({fmtUSD(newShareAfter * SAMPLE_POOL_AMOUNT)})</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-600 mb-1">
              Note (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={`e.g. ${traineeName} completed 3-month training period, ready for solo deals.`}
              className="w-full text-sm border border-slate-300 rounded px-3 py-2"
            />
          </div>

          {error && <div className="text-xs text-red-700 bg-red-50 rounded px-3 py-2">⚠ {error}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
          <button onClick={onClose} disabled={submitting} className="px-4 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50">
            Cancel
          </button>
          <button
            onClick={activate}
            disabled={submitting || !validProposed || !allActiveStillPositive}
            className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? "Activating…" : `Activate ${traineeName}`}
          </button>
        </div>
      </div>
    </div>
  );
}
